import PropTypes from 'prop-types';
import React from 'react';

import { colorList, fontSizeList, fontWeightList } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { DRIVERFLAG, placeholdersMap } from '../../../utils/placeholders-motorsports';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import ImgPlaceholder from '../img-placeholder/img-placeholder';
import Text from '../text/text';

import './flag-and-name.scss';

export interface IFlagAndName extends IComponent {
  flagUrl: string;
  name: string;
  url?: string;
  isUpperCase?: boolean;
  mobileWithOutFlag?: boolean;
  nationality?: string;
  ellipsis?: boolean;
}

const classNames = ({ isUpperCase, url, mobileWithOutFlag, ellipsis, color }: IClassNames): IClassNames =>
  makeClassNames({
    root: ['flag-and-name', mobileWithOutFlag && 'flag-and-name--no-flag'],
    flag: 'flag-and-name__flag',
    name: [
      'flag-and-name__name',
      isUpperCase && 'flag-and-name__name--uppercase',
      url && 'flag-and-name__name--clickable',
      mobileWithOutFlag && 'flag-and-name__name--no-flag',
      ellipsis && 'flag-and-name__name--ellipsis',
      color && `flag-and-name__name--${color}`,
    ],
  });

const FlagAndName: React.FC<IFlagAndName> = ({
  flagUrl,
  name,
  url,
  /* istanbul ignore next: default value on mandatory field */ color = 'blue',
  /* istanbul ignore next: default value on mandatory field */ fontSize = '14',
  /* istanbul ignore next: default value on mandatory field */ fontWeight = '500',
  isUpperCase = false,
  ellipsis = false,
  className,
  mobileWithOutFlag = false,
  nationality,
}) => {
  const cn = classNames({ isUpperCase, url, mobileWithOutFlag, ellipsis, color });

  const flagAndName = (
    <React.Fragment>
      <ImgPlaceholder
        src={flagUrl}
        className={cn.flag}
        placeholder={placeholdersMap[DRIVERFLAG]}
        aria-label={nationality}
        aria-hidden={flagUrl === '-'}
      />
      <Text
        className={[cn.name, className].filter(Boolean).join(' ')}
        color={color}
        fontSize={fontSize}
        fontWeight={fontWeight}
      >
        {name}
      </Text>
    </React.Fragment>
  );

  return url ? (
    <a className={cn.root} href={url}>
      {flagAndName}
    </a>
  ) : (
    <div className={cn.root}>{flagAndName}</div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  FlagAndName.propTypes = {
    className: PropTypes.string,
    color: PropTypes.oneOf(colorList),
    ellipsis: PropTypes.bool,
    flagUrl: PropTypes.string.isRequired,
    fontSize: PropTypes.oneOf(fontSizeList),
    fontWeight: PropTypes.oneOf(fontWeightList),
    isUpperCase: PropTypes.bool,
    mobileWithOutFlag: PropTypes.bool,
    name: PropTypes.string.isRequired,
    url: PropTypes.string,
  };
}

export default FlagAndName;
